import { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { api } from "@/lib/rn3";
import { toast } from "sonner";
import {
  ArrowLeft, Plus, X, Loader2, Trash2, Search, CheckCircle2, Clock, Circle, Flag,
  Calendar as CalIcon, User as UserIcon, ListTodo, AlertTriangle,
} from "lucide-react";
import { useT } from "@/lib/i18n";

const STATUS_META = {
  pending:     { key: "pending",     color: "bg-[#737373]", icon: Circle },
  in_progress: { key: "in_progress", color: "bg-[#C4A252]", icon: Clock },
  complete:    { key: "complete",    color: "bg-[#15803D]", icon: CheckCircle2 },
};
const STATUS_ORDER = ["pending", "in_progress", "complete"];
const STATUS_CYCLE = { pending: "in_progress", in_progress: "complete", complete: "pending" };

const PRIORITY_META = {
  low:    { color: "text-[#737373]", bar: "bg-[#A3A3A3]" },
  normal: { color: "text-[#0A0A0A]", bar: "bg-[#C4A252]" },
  high:   { color: "text-[#8B1C22]", bar: "bg-[#8B1C22]" },
  urgent: { color: "text-white",     bar: "bg-[#8B1C22]" },
};
const PRIORITY_CYCLE = { low: "normal", normal: "high", high: "urgent", urgent: "low" };

const todayISO = () => new Date().toISOString().slice(0, 10);
const isOverdue = (d, status) => !!d && status !== "complete" && d < todayISO();

const StatCard = ({ label, value, accent = "", testid }) => (
  <div className="rn3-card" data-testid={testid}>
    <div className={`mt-1 text-3xl font-serif-display ${accent}`}>{value}</div>
    <div className="text-xs uppercase tracking-widest text-[#737373] mt-1">{label}</div>
  </div>
);

const PriorityBadge = ({ priority, onClick }) => {
  const t = useT();
  const m = PRIORITY_META[priority] || PRIORITY_META.normal;
  return (
    <button
      onClick={onClick}
      title={t("admin.tasks.cycle_priority_title")}
      className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-semibold px-1.5 py-0.5 transition hover:opacity-80 ${priority === "urgent" ? "bg-[#8B1C22] text-white" : m.color}`}
      data-testid={`task-priority-${priority}`}
    >
      <Flag className="w-3 h-3" /> {t(`admin.tasks.priority.${priority}`)}
    </button>
  );
};

const TaskCard = ({ task, onCycleStatus, onCyclePriority, onDelete, onEdit }) => {
  const t = useT();
  const overdue = isOverdue(task.due_date, task.status);
  return (
    <div className="bg-white border border-[#E5E5E5] p-3 hover:border-[#0A0A0A] transition group" data-testid={`task-card-${task.id}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          <div
            className={`text-sm font-medium leading-snug ${task.status === "complete" ? "text-[#737373] line-through" : "text-[#0A0A0A]"} cursor-pointer`}
            onClick={() => onEdit(task)}
            data-testid={`task-title-${task.id}`}
          >
            {task.title}
          </div>
          {task.description && (
            <div className="text-xs text-[#737373] mt-1 line-clamp-2">{task.description}</div>
          )}
        </div>
        <button
          onClick={() => onDelete(task)}
          className="opacity-0 group-hover:opacity-100 text-[#737373] hover:text-[#8B1C22] transition"
          title={t("admin.tasks.delete_title")}
          data-testid={`task-delete-${task.id}`}
        >
          <Trash2 className="w-3.5 h-3.5" />
        </button>
      </div>
      <div className="flex items-center justify-between mt-2 gap-2 flex-wrap">
        <PriorityBadge priority={task.priority} onClick={() => onCyclePriority(task)} />
        {task.due_date && (
          <div className={`inline-flex items-center gap-1 text-[11px] ${overdue ? "text-[#8B1C22] font-semibold" : "text-[#737373]"}`} data-testid={`task-due-${task.id}`}>
            <CalIcon className="w-3 h-3" /> {task.due_date}{overdue && ` · ${t("admin.tasks.overdue_caps")}`}
          </div>
        )}
      </div>
      {task.client && (
        <div className="text-[11px] text-[#737373] mt-1.5 inline-flex items-center gap-1 truncate" data-testid={`task-client-${task.id}`}>
          <UserIcon className="w-3 h-3" /> {task.client.full_name || task.client.email}
        </div>
      )}
      <button
        onClick={() => onCycleStatus(task)}
        className="mt-3 w-full text-[10px] uppercase tracking-widest border border-[#E5E5E5] py-1.5 hover:bg-[#FAFAFA] hover:border-[#0A0A0A] transition"
        data-testid={`task-cycle-${task.id}`}
      >
        → {t("admin.tasks.move_to")} {t(`admin.tasks.status.${STATUS_CYCLE[task.status]}`)}
      </button>
    </div>
  );
};

const TaskEditModal = ({ task, onClose, onSaved }) => {
  const t = useT();
  const [form, setForm] = useState(task);
  const [busy, setBusy] = useState(false);
  const isNew = !task.id;
  useEffect(() => { setForm(task); }, [task]);
  if (!task) return null;

  const save = async () => {
    if (!form.title?.trim()) { toast.error(t("admin.tasks.toast.title_required")); return; }
    setBusy(true);
    try {
      if (isNew) {
        await api.post("/admin/tasks", {
          title: form.title.trim(),
          description: form.description || null,
          status: form.status || "pending",
          priority: form.priority || "normal",
          due_date: form.due_date || null,
          client_id: form.client_id || null,
        });
        toast.success(t("admin.tasks.toast.created"));
      } else {
        await api.patch(`/admin/tasks/${task.id}`, {
          title: form.title.trim(),
          description: form.description ?? "",
          status: form.status,
          priority: form.priority,
          due_date: form.due_date || null,
          client_id: form.client_id || null,
        });
        toast.success(t("admin.tasks.toast.updated"));
      }
      onSaved?.();
      onClose();
    } catch { toast.error(t("admin.tasks.toast.save_failed")); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/40 p-4" onClick={onClose} data-testid="task-edit-modal">
      <div className="bg-white max-w-lg w-full border border-[#E5E5E5] shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#E5E5E5]">
          <div>
            <div className="rn3-eyebrow">{t("admin.tasks.center_eyebrow")}</div>
            <h3 className="font-serif-display text-2xl mt-1">{isNew ? t("admin.tasks.new_task") : t("admin.tasks.edit_task")}</h3>
          </div>
          <button onClick={onClose} className="text-[#737373] hover:text-[#8B1C22]"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-6 space-y-4">
          <div>
            <label className="rn3-eyebrow block mb-2">{t("admin.tasks.field.title")}</label>
            <input value={form.title || ""} onChange={(e) => setForm({ ...form, title: e.target.value })} className="rn3-input w-full" autoFocus placeholder={t("admin.tasks.field.title_placeholder")} data-testid="task-form-title" />
          </div>
          <div>
            <label className="rn3-eyebrow block mb-2">{t("admin.tasks.field.description")}</label>
            <textarea rows={3} value={form.description || ""} onChange={(e) => setForm({ ...form, description: e.target.value })} className="rn3-input w-full" data-testid="task-form-description" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="rn3-eyebrow block mb-2">{t("admin.col.status")}</label>
              <select value={form.status || "pending"} onChange={(e) => setForm({ ...form, status: e.target.value })} className="rn3-input w-full" data-testid="task-form-status">
                {STATUS_ORDER.map((s) => <option key={s} value={s}>{t(`admin.tasks.status.${s}`)}</option>)}
              </select>
            </div>
            <div>
              <label className="rn3-eyebrow block mb-2">{t("admin.tasks.field.priority")}</label>
              <select value={form.priority || "normal"} onChange={(e) => setForm({ ...form, priority: e.target.value })} className="rn3-input w-full" data-testid="task-form-priority">
                {Object.keys(PRIORITY_META).map((p) => <option key={p} value={p}>{t(`admin.tasks.priority.${p}`)}</option>)}
              </select>
            </div>
          </div>
          <div>
            <label className="rn3-eyebrow block mb-2">{t("admin.tasks.field.due_date")}</label>
            <input type="date" value={form.due_date || ""} onChange={(e) => setForm({ ...form, due_date: e.target.value })} className="rn3-input w-full" data-testid="task-form-due" />
          </div>
          <div className="flex justify-end gap-2 pt-3 border-t border-[#E5E5E5]">
            <button onClick={onClose} className="text-sm text-[#737373] hover:text-[#0A0A0A] px-3">{t("common.cancel")}</button>
            <button onClick={save} disabled={busy} className="rn3-btn-primary" data-testid="task-form-save">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} {isNew ? t("admin.tasks.create_task") : t("admin.tasks.save_changes")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const TasksTab = () => {
  const t = useT();
  const { user, loading: authLoading } = useAuth();
  const nav = useNavigate();
  const [tasks, setTasks] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [priorityFilter, setPriorityFilter] = useState("all");
  const [overdueOnly, setOverdueOnly] = useState(false);
  const [editing, setEditing] = useState(null);
  const [quickTitle, setQuickTitle] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (q) params.q = q;
      if (priorityFilter !== "all") params.priority = priorityFilter;
      if (overdueOnly) params.overdue = true;
      const [tr, s] = await Promise.all([
        api.get("/admin/tasks", { params }),
        api.get("/admin/tasks/stats"),
      ]);
      setTasks(tr.data); setStats(s.data);
    } catch { toast.error(t("admin.tasks.toast.load_failed")); }
    finally { setLoading(false); }
  }, [q, priorityFilter, overdueOnly, t]);

  useEffect(() => {
    if (authLoading) return;
    if (!user || user.role !== "admin") { nav("/portal"); return; }
    load();
  }, [user, authLoading, nav, load]);

  const grouped = useMemo(() => {
    const g = { pending: [], in_progress: [], complete: [] };
    for (const tk of tasks) {
      (g[tk.status] || g.pending).push(tk);
    }
    return g;
  }, [tasks]);

  const cycleStatus = async (task) => {
    const next = STATUS_CYCLE[task.status];
    try { await api.patch(`/admin/tasks/${task.id}`, { status: next }); load(); }
    catch { toast.error(t("admin.tasks.toast.update_failed")); }
  };
  const cyclePriority = async (task) => {
    const next = PRIORITY_CYCLE[task.priority];
    try { await api.patch(`/admin/tasks/${task.id}`, { priority: next }); load(); }
    catch { toast.error(t("admin.tasks.toast.update_failed")); }
  };
  const remove = async (task) => {
    if (!window.confirm(`${t("admin.tasks.confirm_delete")} "${task.title}"?`)) return;
    try { await api.delete(`/admin/tasks/${task.id}`); toast.success(t("admin.tasks.toast.deleted")); load(); }
    catch { toast.error(t("admin.tasks.toast.delete_failed")); }
  };
  const quickAdd = async () => {
    const title = quickTitle.trim();
    if (!title) return;
    try {
      await api.post("/admin/tasks", { title, status: "pending", priority: "normal" });
      setQuickTitle("");
      toast.success(t("admin.tasks.toast.added"));
      load();
    } catch { toast.error(t("admin.toast.failed")); }
  };

  return (
    <div className="rn3-container py-10" data-testid="tasks-tab">
      <button onClick={() => nav("/admin")} className="text-xs uppercase tracking-widest text-[#737373] hover:text-[#8B1C22] inline-flex items-center gap-1 mb-4" data-testid="tasks-back">
        <ArrowLeft className="w-3 h-3" /> {t("admin.back_to_admin")}
      </button>

      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <div className="rn3-eyebrow">{t("admin.tasks.center_eyebrow")}</div>
          <h1 className="font-serif-display text-4xl mt-2">{t("admin.tasks.heading")}</h1>
        </div>
        <button onClick={() => setEditing({ status: "pending", priority: "normal" })} className="rn3-btn-primary" data-testid="task-new-btn">
          <Plus className="w-4 h-4" /> {t("admin.tasks.new_task")}
        </button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mt-8">
        <StatCard label={t("admin.tasks.stat.total")} value={stats?.total ?? "—"} testid="stat-task-total" />
        <StatCard label={t("admin.tasks.status.pending")} value={stats?.pending ?? "—"} testid="stat-task-pending" />
        <StatCard label={t("admin.tasks.status.in_progress")} value={stats?.in_progress ?? "—"} testid="stat-task-in-progress" />
        <StatCard label={t("admin.tasks.status.complete")} value={stats?.complete ?? "—"} testid="stat-task-complete" />
        <StatCard label={t("admin.tasks.stat.overdue")} value={stats?.overdue ?? "—"} accent={stats?.overdue > 0 ? "text-[#8B1C22]" : ""} testid="stat-task-overdue" />
      </div>

      <div className="flex flex-wrap items-end gap-3 mt-8 border-t border-[#E5E5E5] pt-6">
        <div className="flex-1 min-w-[240px]">
          <label className="rn3-eyebrow block mb-2">{t("common.search")}</label>
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#737373] pointer-events-none" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("admin.tasks.search_placeholder")} className="rn3-input w-full !pl-10" data-testid="task-search" />
          </div>
        </div>
        <div>
          <label className="rn3-eyebrow block mb-2">{t("admin.tasks.field.priority")}</label>
          <select value={priorityFilter} onChange={(e) => setPriorityFilter(e.target.value)} className="rn3-input" data-testid="task-filter-priority">
            <option value="all">{t("admin.tasks.all_priorities")}</option>
            {Object.keys(PRIORITY_META).map((p) => <option key={p} value={p}>{t(`admin.tasks.priority.${p}`)}</option>)}
          </select>
        </div>
        <label className="rn3-eyebrow inline-flex items-center gap-2 cursor-pointer" data-testid="task-filter-overdue">
          <input type="checkbox" checked={overdueOnly} onChange={(e) => setOverdueOnly(e.target.checked)} className="accent-[#8B1C22]" />
          <span className="inline-flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> {t("admin.tasks.overdue_only")}</span>
        </label>
      </div>

      {/* Quick-add row */}
      <div className="mt-6 flex items-center gap-2 border border-[#E5E5E5] bg-[#FDFBF7] px-3 py-2">
        <ListTodo className="w-4 h-4 text-[#C4A252] shrink-0" />
        <input
          value={quickTitle}
          onChange={(e) => setQuickTitle(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") quickAdd(); }}
          placeholder={t("admin.tasks.quick_add_placeholder")}
          className="rn3-input flex-1 border-0 bg-transparent focus:ring-0"
          data-testid="task-quick-input"
        />
        <button
          onClick={quickAdd}
          disabled={!quickTitle.trim()}
          className={`rn3-btn-gold text-xs transition-opacity ${!quickTitle.trim() ? "opacity-40 cursor-not-allowed" : ""}`}
          title={!quickTitle.trim() ? t("admin.tasks.quick_add_disabled_title") : t("admin.tasks.add")}
          data-testid="task-quick-add"
        >
          <Plus className="w-3.5 h-3.5" /> {t("admin.tasks.add")}
        </button>
      </div>

      {/* Kanban */}
      {loading && (
        <div className="py-12 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-[#C4A252]" /></div>
      )}
      {!loading && (
        <div className="grid md:grid-cols-3 gap-4 mt-6" data-testid="tasks-kanban">
          {STATUS_ORDER.map((statusKey) => {
            const meta = STATUS_META[statusKey];
            const list = grouped[statusKey] || [];
            const Icon = meta.icon;
            return (
              <div key={statusKey} className="bg-[#FAFAFA] border border-[#E5E5E5] p-3 flex flex-col min-h-[400px]" data-testid={`tasks-column-${statusKey}`}>
                <div className="flex items-center justify-between mb-3">
                  <div className="inline-flex items-center gap-2">
                    <span className={`inline-flex items-center justify-center w-5 h-5 ${meta.color} text-white`}><Icon className="w-3 h-3" /></span>
                    <span className="text-sm font-semibold uppercase tracking-widest">{t(`admin.tasks.status.${statusKey}`)}</span>
                  </div>
                  <span className="text-xs text-[#737373]">{list.length}</span>
                </div>
                <div className="space-y-2 flex-1">
                  {list.length === 0 && <div className="text-xs text-[#737373] italic py-3 text-center">{t("admin.tasks.empty_column")}</div>}
                  {list.map((tk) => (
                    <TaskCard
                      key={tk.id}
                      task={tk}
                      onCycleStatus={cycleStatus}
                      onCyclePriority={cyclePriority}
                      onDelete={remove}
                      onEdit={setEditing}
                    />
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {editing && (
        <TaskEditModal task={editing} onClose={() => setEditing(null)} onSaved={load} />
      )}
    </div>
  );
};

export default TasksTab;
